import { Component, Inject, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';

import { v4 as uuidv4 } from 'uuid';

import JhiDataUtils from '@/shared/data/data-utils.service';
import AlertService from '@/shared/alert/alert.service';
import { TipoAccion } from '@/shared/model/enumerations/tipo-accion.model';
import camelCase from 'camelcase';
import { normalizeSync } from 'normalize-diacritics';
import { BitacoraDocumento, File, IBitacoraDocumento } from '@/shared/model/file.model';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import DocumentoService from '@/components/documentos/documento.service';
import { Configuracion } from '@/shared/model/configuracion/configuracion.model';
import { DmsApi, EtapaEnum, Archivo } from '@conacyt/dms-client';

import * as dateUtils from '@/shared/date/date-utils';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import { MenuElement } from '@/shared/model/enumerations/menu-element.model';
import GrantService from '@/shared/security/grant.service';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import ScriptService from '@/shared/script/script.service';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';
/**
 *Componente para el guardado de documentos en DMS
 */
@Component
export default class DocumentosApoyosComponent extends mixins(JhiDataUtils) {
  @Inject('documentoService') private readonly documentoService: () => DocumentoService;
  @Inject('alertService') private readonly alertService: () => AlertService;
  @Inject('dmsApi') private readonly dmsApi: () => DmsApi;
  @Inject('grantService') private readonly grantService: () => GrantService;
  @Inject('scriptService') private readonly scriptService: () => ScriptService;

  @Prop({ required: true })
  public readonly solucion: Solucion;

  @Prop({ default: false })
  public mock: boolean;

  @Prop({ required: false })
  public readonly solicitud: Solicitud;

  @Prop({ default: false })
  public saving: boolean;

  @Prop({ required: false })
  public readonly permisos: any;

  /**
   * Variable para guardar nombre del archivo obtenido de las configuraciones
   */
  public newFileName = '';

  /**
   * Variable para almacenar el listado de comentarios obtenidos de bitacora
   */
  public comentarios: IBitacoraDocumento[] = new Array<IBitacoraDocumento>();

  private readonly ETAPA: EtapaEnum = 'REGISTRO';

  /**
   * Variable para mostrar la metadata del archivo almacenado en DMS
   */
  public documentoModel: any = {};

  public isLoading = false;

  /**
   * Variale donde se almacena la información del archivo que ha seleccionado
   */
  public currentFile: any = null;

  /**
   * Variable para almacenar el documento seleccionado en BASE64
   */
  public currentContent: any = null;

  /**
   *  Esta  vairable se utiliza para saber qué archivo se está  actualizando
   */
  public currentFileIndex = -1;

  public tags = [];

  public pasosPreviosMovilidad = false;
  public pasosPreviosEspMed = false;

  /**
   * Listado de opciones
   */
  public readonly options = [
    { text: 'Aceptar', value: true },
    { text: 'Rechazar', value: false },
  ];
  /**
   *
   */
  public executions: any[];

  /**
   * Bandera para mostrar el campo de curp para los apoyos complementarios maternidad y paternidad
   */
  public showFieldCurp = false;

  /**
   * Propiedades para eliminar documentos
   */
  private docDelete: File = new File();
  public deleteModalVisible = false;

  /**
   * Obtiene las configuraciones realizadas en el formulario
   */
  get configuracion(): Configuracion {
    return this.solucion.componentes.find(com => com.menuName === MenuElement.DOCUMENTOS).configuracion;
  }

  get isOperador(): boolean {
    return this.solucion.autoridades.some(
      user =>
        user.usuarioId == this.userInfo.login &&
        user.roles.some(
          rol =>
            rol === RolAutoridad.OPERADOR ||
            rol === RolAutoridad.JEFE_DEPARTAMENTO ||
            rol === RolAutoridad.SUBDIRECTOR ||
            rol === RolAutoridad.APOYO
        )
    );
  }

  /**
   * Obtiene los accesos permitidos en las acciones configurados en la solucion
   */
  get accesos(): any {
    return this.grantService().accesos(this.solucion, this.solicitud);
  }

  /**
   * Obtiene bandera para indicar de solo lectura basado en la accion de Escribir
   */
  get readOnly(): boolean {
    if (this.accesos.acciones.findIndex(accion => accion === TipoAccion.ESCRIBIR) === -1) {
      return true;
    }
    return false;
  }

  /**
   * Obtiene bandera que permite comentar los archivos validados en el envió de la solicitud
   */
  get permiteComentar(): boolean {
    return (
      (this.solicitud.estado == EstadoSolicitud.ENVIADA || this.solicitud.estado == EstadoSolicitud.REVISADA) &&
      this.accesos.acciones.findIndex(accion => accion === TipoAccion.COMENTAR) !== -1
    );
  }

  /**
   *Obtiene las propiedades de la configuración de los documentos a mostrar en pantalla
   */
  get properties(): any[] {
    const propertiesWithConfig = this.configuracion.documentos
      .map(documento => documento.nombrePropiedad)
      .map(property => {
        const propertyDefinition = {
          key: property,
          config: { title: null, max: null, mediaTypes: [], requerido: false, orden: 1000, expresion: null },
        };
        const configurationIndex = this.configuracion.documentos.findIndex(config => config.nombrePropiedad === property);
        return configurationIndex === -1
          ? this.mapFromGeneralConfiguration(propertyDefinition, property)
          : this.mapFromConfiguration(propertyDefinition, configurationIndex);
      });
    return this.order(propertiesWithConfig);
  }

  get canAddMoreDocuments(): boolean {
    if (this.configuracion.hasMoreDocuments && !this.configuracion.maxNumberOfDocuments) {
      return true;
    }

    if (this.configuracion.hasMoreDocuments && this.configuracion.maxNumberOfDocuments) {
      return this.propertiesNotInConfiguration.length <= this.configuracion.maxNumberOfDocuments;
    }

    return false;
  }

  get propertiesNotInConfiguration(): string[] {
    const fullDocumentProperties = Object.keys(this.documentoModel);
    return fullDocumentProperties.filter(
      property => this.configuracion.documentos.findIndex(config => config.nombrePropiedad === property) === -1
    );
  }

  /**
   * Obtiene la información del usuario actual
   */
  get userInfo(): any {
    return this.$store.getters.userInfo;
  }

  get configuracionColumnasComent(): any {
    return [
      {
        key: 'fecha',
        label: this.$t('archivosApp.documento.comentarios.fecha').toString(),
        class: 'text-center',
        sortable: false,
        formatter: fecha => {
          return dateUtils.formatDate(fecha);
        },
      },
      {
        key: 'descripcion',
        label: this.$t('archivosApp.documento.comentarios.comentario').toString(),
        class: 'text-center',
        sortable: false,
      },
      {
        key: 'archivo',
        label: this.$t('archivosApp.documento.comentarios.documento').toString(),
        class: 'text-center',
        sortable: false,
      },
    ];
  }

  /**
   * Inicialización de pantalla
   */
  created(): void {
    this.tags = this.solucion.tags;
    if (this.solicitud) {
      this.pasosPreviosMovilidad =
        !!this.solicitud.beca?.id &&
        !!this.solicitud.apoyoSolicitado?.apoyo &&
        this.solicitud.cronogramaActividades?.length >= 3 &&
        !!this.solicitud.institucion.id &&
        !!this.solicitud.cotutor?.apellidoPaterno;
      this.pasosPreviosEspMed =
        !!this.solicitud.beca?.id &&
        this.solicitud.cronogramaActividades?.length >= 3 &&
        !!this.solicitud.institucion.id &&
        !!this.solicitud.cotutor?.apellidoPaterno;
      this.retrieveDocumento(this.solicitud.id, this.solicitud);
    } else {
      this.documentoModel = this.createProperties(this.documentoModel);
      this.setDataSolucionMaternidad();
    }
  }

  public mounted(): void {
    if (!this.$store.getters.userInfo) {
      window.location.reload();
    }
  }

  public isCapturable(): boolean {
    const tags = this.solucion.tags;
    if (tags.includes('MovilidadNacional')) {
      return this.pasosPreviosMovilidad;
    }
    if (tags.includes('MovilidadEspecialidadMedica')) {
      return this.pasosPreviosEspMed;
    }
    return true;
  }

  /**
   * Obtiene la información de los documentos guardados en base de datos
   * @param solicitudId
   */
  public retrieveDocumento(solicitudId: string, solicitud?: Solicitud): void {
    this.isLoading = true;
    this.documentoService()
      .findBySolicitudId(solicitudId)
      .then(res => {
        this.isLoading = false;
        this.documentoModel = this.createProperties(res);
        this.setDataSolucionMaternidad();
      })
      .catch(error => {
        this.isLoading = false;
        this.documentoModel = this.createProperties(this.documentoModel);
        this.setDataSolucionMaternidad();
      });
  }

  /**
   * Se crean las propiedades de un documento
   * a partir de la configuración del archivo.
   * @param model
   * @returns
   */
  public createProperties(model: any): any {
    if (this.configuracion) {
      /** Se agregan las propiedades qué provienen del archivo de configuración */
      this.configuracion.documentos.forEach(config => {
        if (!model[config.nombrePropiedad]) {
          /**Es necesario utilizar el método this.$set()
           * para qué las propiedades puedan ser reactivas */
          this.$set(model, config.nombrePropiedad, new File());
        }
      });
    }
    return model;
  }

  /**
   * Proceso para mostrar el campo de la CURP validada, a fin de asignarlo a un archivo
   * que será requerido para la gestión de los apoyos de movilidad por maternidad y paternidad.
   */
  private setDataSolucionMaternidad() {
    const { solicitud, solucion, documentoModel } = this;

    // Validación temprana para salir si no aplica
    const isMaternidad = solucion?.tags?.some(tag => ['MovilidadMaternidadNacional', 'MovilidadMaternidadExtranjera'].includes(tag));
    const isApoyoComplementario = [TipoSolucion.APOYOS_COMPLEMENTARIOS_BN, TipoSolucion.APOYOS_COMPLEMENTARIOS_EXT].includes(
      solucion?.tipo
    );

    if (!solicitud || !isMaternidad || !isApoyoComplementario) {
      return;
    }

    this.showFieldCurp = true;
    const normalize = (str: string) =>
      str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

    const properties = Object.keys(documentoModel).filter(p => p !== 'id');

    const actasProperties = properties.filter(p => !/Identificacion/i.test(normalize(p)));
    const idVigenteProperties = properties.filter(p => /Identificacion/i.test(normalize(p)));

    const hijos = solicitud?.datosHijos ?? [];
    actasProperties.forEach((property, index) => {
      const hijo = hijos[index];
      if (!hijo) return; // seguridad por si el índice no existe
      documentoModel[property].curp = hijo.curp;
    });

    idVigenteProperties.forEach((property, index) => {
      const hijo = hijos[index];
      if (!hijo) return; // seguridad por si el índice no existe
      if (hijo.discapacidad === 'si') {
        documentoModel[property].curp = hijo.curp;
      }
    });
  }

  private order(propertyDefinition: any[]): any {
    let count = 0;
    this.configuracion.documentos.forEach(documento => {
      const index = propertyDefinition.findIndex(property => property.key === documento.nombrePropiedad);
      propertyDefinition[index].config.orden = count++;
    });

    return propertyDefinition.sort((leftProperty, rightProperty) => leftProperty.config.orden - rightProperty.config.orden);
  }

  private mapFromConfiguration(propertyDefinition: any, configurationIndex: number): any {
    const config = this.configuracion.documentos[configurationIndex];
    propertyDefinition.config.title = config.nombre;
    propertyDefinition.config.max = config.max;
    propertyDefinition.config.mediaTypes = config.mediaTypes;
    propertyDefinition.config.requerido = !!config.requerido;
    propertyDefinition.config.expresion = config.expresion ? config.expresion : 'return true;';
    return propertyDefinition;
  }

  private mapFromGeneralConfiguration(propertyDefinition: any, property: string): any {
    propertyDefinition.config.title = this.documentoModel[property].title;
    propertyDefinition.config.max = this.configuracion.maxFileSize;
    propertyDefinition.config.mediaTypes = this.configuracion.mediaTypes;
    propertyDefinition.config.requerido = false;
    propertyDefinition.config.expresion = this.configuracion.expresion;
    return propertyDefinition;
  }

  public addPropertyToModel(model: any, propertyName: string, nombreDocumento?: string): void {
    const newFile = new File();
    newFile.title = nombreDocumento;
    newFile.comentarios = new Array<IBitacoraDocumento>();
    this.$set(model, propertyName, newFile);
  }

  /**
   * Agrega comentario a la lista de comentarios
   *
   */
  public agregarComentario(property: string): void {
    const coment = new BitacoraDocumento(
      this.documentoModel[property].comentario,
      new Date(),
      this.documentoModel[property].uri,
      this.documentoModel[property]?.check ?? null,
      this.userInfo.login,
      uuidv4(),
      this.documentoModel[property].nombre
    );
    if (!this.documentoModel[property]?.comentarios) {
      this.documentoModel[property].comentarios = new Array<IBitacoraDocumento>();
    }
    this.documentoModel[property].comentarios.push(coment);
    this.documentoModel[property].comentario = null;
    this.save(false, true);
  }

  /**
   * Guarda la información de la metadata del documento que sera guardado al DMS
   * @param isFullSave Si sera un guardado total
   * @param isUpdate SI solo se actualizara el documento
   * @returns void
   */
  public save(isFullSave?: boolean, isUpdate?: boolean): void {
    if (!this.solicitud.id) {
      return;
    }
    const documentModel = this.validaInformacionDocumentoModel(isUpdate);
    if (documentModel.id) {
      this.documentoService()
        .update(documentModel)
        .then(param => {
          this.currentFileIndex = -1;
          this.setDocumentosModel(param);
          this.setDataSolucionMaternidad();
          if (isFullSave) {
            this.$emit('documentoSaved');
          }
          this.alertService().sectionUpdated(this, 'Documentos');
        })
        .catch(error => {
          this.currentFileIndex = -1;
          this.alertService().showHttpError(this, error.response);
        });
    } else {
      documentModel.id = this.solicitud.id;
      this.documentoService()
        .create(documentModel)
        .then(param => {
          this.setDocumentosModel(param);
          this.setDataSolucionMaternidad();
          this.currentFileIndex = -1;
          this.alertService().sectionUpdated(this, 'Documentos');
        })
        .catch(error => {
          this.currentFileIndex = -1;
          this.alertService().showHttpError(this, error.response);
        });
    }
  }

  private setDocumentosModel(documentoParam: any) {
    this.documentoModel.id = documentoParam.id;

    this.configuracion.documentos
      .map(documento => {
        return documento.nombrePropiedad;
      })
      .forEach(property => {
        if (documentoParam[property]) {
          this.documentoModel[property] = documentoParam[property];
        } else {
          const propertyDefinition = {
            contentType: '',
            nombre: '',
            title: '',
            uri: null,
            check: null,
          };
          this.documentoModel[property] = propertyDefinition;
        }
      });
  }

  /**
   * Valida la información del modelo del documento
   * @param isUpdate
   * @returns
   */
  private validaInformacionDocumentoModel(isUpdate?: boolean): any {
    const properties = Object.keys(this.documentoModel);
    const documentModel: any = {};
    if (this.documentoModel.id) {
      documentModel.id = this.documentoModel.id;
    }
    properties.forEach(property => {
      if (
        (isUpdate && this.documentoModel[property]?.contentType) ||
        (this.documentoModel[property]?.uri &&
          this.documentoModel[property]?.contentType &&
          this.documentoModel[property]?.contentType?.length !== 0)
      ) {
        documentModel[property] = this.documentoModel[property];
      }
    });
    return documentModel;
  }

  /**
   * Carga y convierte archivo a Base64 que será enviado a DMS
   * @param file
   * @param doc
   * @param fileIndex
   */
  public onReadFile(file, doc: File, fileIndex): void {
    if (file) {
      this.currentFileIndex = fileIndex;
      this.toBase64(file, base64Data => {
        doc.nombre = file.name.replace('.pdf', '-' + dateUtils.formatDateHoraSegundos(new Date()) + '.pdf');
        doc.contentType = file.type;
        this.currentContent = base64Data;
        this.guardarDocumento(doc);
      });
    }
  }

  /**
   * Guarda documento en el DMS
   * @param doc El Documento
   */
  public guardarDocumento(doc: File): void {
    const archivo: Archivo = { nombre: doc.nombre, contenido: this.currentContent };
    this.dmsApi()
      .agregaDocumento(this.configuracion.carpeta, this.solicitud.id, this.userInfo.cvu, this.ETAPA, undefined, archivo)
      .then(res => {
        doc.uri = res.headers.location;
        this.currentContent = null;
        this.currentFile = null;
        this.currentFileIndex = -1;
        this.save();
      })
      .catch(error => {
        this.currentContent = null;
        this.currentFile = null;
        this.currentFileIndex = -1;
        this.alertService().showHttpError(this, error.response);
      });
  }

  /**
   * Procesa documento que ha sido reemplazado
   * @param doc Documento a reemplazar
   * @param index Indice de documento
   */
  public handleDeleteDocumento(doc: File, index): void {
    this.currentFileIndex = index;
    this.docDelete = doc;
    this.deleteModalVisible = true;
  }

  /**
   * Procesa documento que ha sido reemplazado
   * @param doc Documento a reemplazar
   * @param index Indice de documento
   */
  public doDeleteDocumento(): void {
    this.deleteModalVisible = false;
    if (!!this.docDelete) {
      this.docDelete.uri = null;
      this.docDelete.check = null;
      this.save(false, true);
      this.currentFileIndex = -1;
    }
  }

  /**
   * Cancela la eliminación del documento
   */
  public cancelDeleteDocumet(): void {
    this.deleteModalVisible = false;
    this.docDelete = new File();
    this.currentFileIndex = -1;
  }

  /**
   *  Extensiones permitidas que se pueden cargar a DMS
   * @param mediaTypes Extensiones permitidas
   * @returns
   */
  public toFileExtension(mediaTypes: string[]): string {
    if (!mediaTypes) {
      return '.pdf';
    }
    let fileExtensions = '';
    mediaTypes.forEach((type, index) => {
      if (index === 0) {
        fileExtensions = '.' + type;
      } else {
        fileExtensions = fileExtensions + ', .' + type;
      }
    });
    return fileExtensions;
  }

  /**
   * Procesa documento que se va a cargar a DMS
   */
  public handleAddDocument(): void {
    const propertyName = camelCase(normalizeSync(this.newFileName));
    this.addPropertyToModel(this.documentoModel, propertyName, this.newFileName);
    this.newFileName = '';
    this.hideModal();
  }

  public handleOpenAddNewDocumentModal(): void {
    (this.$refs.addNewDocument as any).show();
  }

  public handleCloseAddDocumentModal(): void {
    this.hideModal();
  }

  public handleCancel(): void {
    this.hideModal();
  }

  public hideModal(): void {
    (this.$refs.addNewDocument as any).hide();
  }

  /**
   * Setea el indice de archivo que se va actualizar
   * @param fileIndex Index de archivo
   * @returns Boolean
   */
  public isUpdating(fileIndex): boolean {
    return this.currentFileIndex === fileIndex;
  }

  public showValidation(): boolean {
    return this.solicitud.estado !== EstadoSolicitud.EN_CAPTURA;
  }

  /**
   * Valida acciones permitidas en los eventos
   * @returns
   */
  public disabledValidation(): boolean {
    if (this.accesos.acciones) {
      return (
        this.accesos.acciones.filter(accion => {
          return [TipoAccion.VALIDAR, TipoAccion.RECHAZAR, TipoAccion.APROBAR, TipoAccion.REVISAR].includes(accion);
        }).length <= 0
      );
    }
    return true;
  }

  /**
   * Verifica que el documento sea o no editable dependiendo del rol, estado de solicitud y opción seleccionada: aceptado, rechazado, no aplica
   * @param documento
   * @returns Boolean
   */
  public esNoEditable(documento: any): boolean {
    return (
      (this.solicitud.estado == EstadoSolicitud.EN_CORRECCION &&
        documento.check != null &&
        (documento.check === true || documento.check == 'NA')) ||
      !this.grantService().isSolicitante(this.solicitud, this.grantService().getUsername(), this.grantService().getUsercvu)
    );
  }

  /**
   * Abre el modal de la lista de comentarios
   *
   */
  public handleOpenComentariosModal(property: string): void {
    this.comentarios = this.documentoModel[property].comentarios;
    (this.$refs.verComentarios as any).show();
  }

  /**
   * Cierra el modal de la lista de comentarios
   *
   */
  public hideModalComentarios(): void {
    (this.$refs.verComentarios as any).hide();
  }

  /**
   * Ejecuta la expresion(condición) para mostrar los archivos
   * @param expression
   * @returns any
   */
  public execute(expression) {
    const context = { solucion: this.solucion, solicitud: this.solicitud };
    return this.scriptService().runFunction(expression, context);
  }

  /**
   * Valida solicitud EN_CAPTURA y que el usuario sea el solicitante
   * @returns boolean
   */
  public isSaving(): boolean {
    return (
      this.solicitud.estado === EstadoSolicitud.EN_CAPTURA &&
      this.grantService().isSolicitante(this.solicitud, this.grantService().getUsername(), this.grantService().getUsercvu)
    );
  }

  /**
   *
   * @param doc El archivo a actualizar
   */
  public handleAceptDocument(doc: File): void {
    if (doc.check === true) this.save(false, true);
  }

  /**
   * Condición para desabilitar el campo de CURP
   * @param field objeto de datos
   * @returns
   */
  public esNoEditableField(field?: any): boolean {
    return (
      field?.curp == undefined ||
      !this.grantService().isSolicitante(this.solicitud, this.grantService().getUsername(), this.grantService().getUsercvu)
    );
  }
}
